import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Counter, Reveal, RevealGroup, revealItem } from "@/components/flow/Reveal";
import { PrimaryButton, SecondaryButton } from "@/components/flow/Primitives";
import { BrandLogo } from "@/components/flow/BrandLogos";
import { WorkflowFit } from "@/components/flow/WorkflowFit";
import { SiteHeader, SiteFooter, CONTAINER, EYEBROW, H2, SUB } from "@/pages/home/SiteChrome";
import { SupportChat } from "@/components/flow/SupportChat";
import { JourneyLoop } from "@/pages/early-access/JourneyLoop";
import { HowItWorks } from "@/pages/home/HowItWorks";
import { BrandPreviewForm, BrandPreviewProvider, useBrandPreview } from "@/pages/home/BrandPreview";
import { pageBrandStyle } from "@/lib/brandColor";
import { tb } from "@/lib/brandify";
import { publicApi } from "@/lib/api";
import { ProblemSection } from "@/pages/home/ProblemSection";
import { StoryScroll } from "@/pages/home/StoryScroll";
import { WordReveal, Rise, useFontsReady } from "@/components/flow/TextReveal";
import { t } from "@/lib/i18n";
import { applyMeta, resolveMeta } from "@/lib/seo";
import { EASE } from "@/lib/motion";
import { cn } from "@/lib/utils";

/* Below-the-fold sections are split out of the first bundle and prefetched as
   soon as the browser is idle, so the hero paints sooner and nothing pops in. */
const loadCapabilities = () => import("@/pages/home/CapabilitiesSection");
const loadWhiteLabel = () => import("@/pages/home/WhiteLabelSection");
const loadIntegrations = () => import("@/pages/home/IntegrationsSection");
const CapabilitiesSection = lazy(() => loadCapabilities().then((m) => ({ default: m.CapabilitiesSection })));
const WhiteLabelSection = lazy(() => loadWhiteLabel().then((m) => ({ default: m.WhiteLabelSection })));
const IntegrationsSection = lazy(() => loadIntegrations().then((m) => ({ default: m.IntegrationsSection })));
const SectionFallback = ({ h = "min-h-[720px]" }) => <div className={cn(h, "border-t border-hairline")} aria-hidden />;

/* ---------------------------------------------------------------------------
   Landing v10 — quiet, product-first, with more depth and rhythm.
   Type, hairlines and the product carry the page; motion is transform/opacity
   only and always explains a state change. Atmosphere is limited to the hero
   (masked dot grid + one soft lavender glow) and the final CTA.
--------------------------------------------------------------------------- */

const DEMO_SLUG = "northstay";
export { EYEBROW, H2, SUB };

/* ================================================================== */
/* Desktop side rail — where you are on the page, and a way to jump.   */
const DOTS = [
  ["how", "hiw.eyebrow"],
  ["story", "l12.story.eyebrow"],
  ["problem", "l11.pb.eyebrow"],
  ["capabilities", "l4.cap.eyebrow"],
  ["whitelabel", "lp.nav.whitelabel"],
  ["integrations", "lp.nav.integrations"],
  ["faq", "l10.faq.eyebrow"],
];
function SectionDots() {
  const [active, setActive] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    // Desktop only — the rail is hidden below lg, so never pay for the per-frame measuring there.
    const mq = window.matchMedia("(min-width: 1024px)");
    if (!mq.matches) return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const vh = window.innerHeight;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - vh;
      setShow(y > vh * 0.9 && y < max - vh * 0.9);
      const mid = vh * 0.5;
      let cur = null;
      for (const [id] of DOTS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= mid && r.bottom >= mid) cur = id;
      }
      setActive(cur);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <nav aria-label={t("home.steps")} className={cn("hidden lg:flex fixed right-5 xl:right-7 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-3.5 mix-blend-difference text-white transition-opacity duration-300", show ? "opacity-100" : "opacity-0 pointer-events-none")} data-testid="section-dots" data-active={active || ""}>
      {DOTS.map(([id, key]) => {
        const on = active === id;
        return (
          <a key={id} href={`#${id}`} aria-current={on ? "true" : undefined} className="group flex items-center gap-3 h-6" data-testid={`section-dot-${id}`}>
            <span className="text-[11.5px] font-medium tracking-[0.01em] transition-[opacity,transform] duration-200 ease-editorial translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0">{t(key)}</span>
            <span className={cn("block h-1.5 rounded-full bg-white transition-[width,opacity] duration-300 ease-editorial", on ? "w-6 opacity-100" : "w-1.5 opacity-50 group-hover:opacity-90")} />
          </a>
        );
      })}
    </nav>
  );
}

/* ================================================================== */
export default function HomePage() {
  useEffect(() => {
    // Prefetch the split sections once the main thread is idle; then honour a deep link (/#journey).
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 600));
    const id = idle(() => {
      Promise.all([loadCapabilities(), loadWhiteLabel(), loadIntegrations()]).then(() => {
        const target = window.location.hash.slice(1);
        if (!target) return;
        setTimeout(() => document.getElementById(target)?.scrollIntoView({ block: "start" }), 80);
      });
    });
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);
  return (
    <BrandPreviewProvider>
      <HomeBody />
    </BrandPreviewProvider>
  );
}

/** The page itself. When a visitor's brand is active the whole page adopts it: every lavender token
    (headline accent, buttons, dots, glows) fades into their colour, and the URL becomes /for/{domain}. */
function HomeBody() {
  const { brand, preview, setPreview, setLoading } = useBrandPreview();
  const { token, domain } = useParams();
  const { pathname } = useLocation();

  // Deep links: /preview/:token (shared) and /for/:domain (sales / typed) load the brand before the page settles.
  useEffect(() => {
    if (!token && !domain) return undefined;
    if (preview && ((token && preview.token === token) || (domain && preview.domain === domain.toLowerCase()))) return undefined;
    let alive = true;
    const run = async () => {
      try {
        if (domain) setLoading(domain);
        const r = token ? await publicApi.get(`/public/brand-preview/${token}`) : await publicApi.get(`/public/brand-preview/by-domain/${encodeURIComponent(domain)}`);
        if (alive) setPreview(r.data);
      } catch {
        /* unknown link → the demo brand */
      } finally {
        if (alive) setLoading("");
      }
    };
    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, domain]);

  // Keep the address bar honest without a navigation: a brand → /for/{domain}; back to demo → /.
  useEffect(() => {
    const marketing = pathname === "/" || pathname.startsWith("/for/") || pathname.startsWith("/preview/");
    if (!marketing) return;
    const want = preview ? `/for/${preview.domain}` : "/";
    if (window.location.pathname !== want) window.history.replaceState(window.history.state, "", want + window.location.hash);
  }, [preview, pathname]);

  // Personalised pages are for the person they were made for — never for search engines.
  // (The URL morphs via replaceState, so the router-level <PageMeta/> does not see it; we write the tags here.)
  useEffect(() => {
    if (preview) {
      const base = resolveMeta(`/for/${preview.domain}`);
      applyMeta({ ...base, title: `Listed for ${preview.name} — win new owners from ${preview.domain}` });
    } else {
      applyMeta(resolveMeta(window.location.pathname.startsWith("/preview/") ? window.location.pathname : "/"));
    }
  }, [preview]);

  return (
    <div className="mkt min-h-screen bg-canvas text-ink page-brand-scope" style={brand ? pageBrandStyle(brand.color) : undefined} data-testid="home-page" data-brand={brand ? brand.name : "demo"}>
      <SiteHeader partner={brand} />
      <Hero />
      <HowItWorks container={CONTAINER} />
      <StoryScroll container={CONTAINER} />
      <TrustLine />
      <ProblemSection container={CONTAINER} />
      <NumbersBand />
      {/* On phones the renderer skips these until they are near the viewport (content-visibility) */}
      <div className="cv-auto" style={{ "--cis": "2800px" }}><Suspense fallback={<SectionFallback />}><CapabilitiesSection container={CONTAINER} /></Suspense></div>
      <div className="cv-auto" style={{ "--cis": "1520px" }}><Suspense fallback={<SectionFallback />}><WhiteLabelSection container={CONTAINER} /></Suspense></div>
      <div className="cv-auto" style={{ "--cis": "2540px" }}><Suspense fallback={<SectionFallback />}><IntegrationsSection container={CONTAINER} /></Suspense></div>
      <div className="cv-auto" style={{ "--cis": "690px" }}><FaqSection /></div>
      <FinalCTA />
      <SiteFooter />
      <SectionDots />
      <SupportChat />
    </div>
  );
}

function Hero() {
  const reduced = useReducedMotion();
  const fonts = useFontsReady();
  const play = fonts || !!reduced;
  const { brand, preview, loading } = useBrandPreview();
  return (
    <section className="relative pt-[68px] overflow-x-clip" data-testid="hero-section" data-fonts={fonts ? "ready" : "loading"} data-brand={brand ? brand.name : "demo"} data-loading={loading ? "true" : "false"} data-state={loading ? "loading" : preview ? "yours" : "demo"}>
      <div className="hero-atmos" aria-hidden />
      <div className={cn(CONTAINER, "relative pt-6 sm:pt-12 lg:pt-10 pb-4 lg:pb-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center")}>
        {/* Left — the promise and the one action */}
        <div className="min-w-0 lg:col-span-6 max-w-[620px]">
          <Rise delay={0.05} duration={0.9} reduced={reduced} play={play}>
            <p className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface/80 pl-2.5 pr-3 h-7 text-[12px] font-medium text-ink/70" data-testid="hero-eyebrow">
              <span className="relative flex h-1.5 w-1.5 rounded-full bg-lavender brand-tint">{loading && <span className="absolute inset-0 rounded-full bg-lavender animate-ping opacity-70" />}</span>
              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={loading ? "l" : preview ? "y" : "d"} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.25, ease: EASE }} data-testid="hero-showing" data-state={loading ? "loading" : preview ? "yours" : "demo"}>
                  {loading ? t("l12.hero.reading", { domain: loading }) : preview ? t("l12.hero.showing_yours", { name: preview.name, domain: preview.domain }) : t("l12.hero.eyebrow")}
                </motion.span>
              </AnimatePresence>
            </p>
          </Rise>
          <WordReveal as="h1" text={t("l12.hero.title")} accentFirst={3} delay={0.15} stagger={0.07} duration={1.1} distance={22} reduced={reduced} play={play} className="mt-5 font-display text-[42px] sm:text-[58px] lg:text-[64px] xl:text-[72px] text-ink text-balance" data-testid="hero-title" />
          <Rise delay={0.55} duration={1.0} reduced={reduced} play={play}>
            <p className="mt-5 sm:mt-6 max-w-[52ch] text-[16px] sm:text-[17.5px] text-ink/60 leading-[1.55]" data-testid="hero-sub">{preview ? tb("l12.hero.sub_brand", { name: preview.name, domain: preview.domain }) : tb("l12.hero.sub")}</p>
          </Rise>
          <Rise delay={0.75} duration={1.0} reduced={reduced} play={play} className="mt-7 sm:mt-8">
            <BrandPreviewForm />
          </Rise>
          {/* One quiet compatibility line — same words as /early-access. Desktop only; the phone carries the fold on mobile. */}
          <Rise delay={1.05} duration={1.0} reduced={reduced} play={play} className="mt-10 hidden lg:block">
            <WorkflowFit testId="hero-fit" />
          </Rise>
        </div>

        {/* Right — the whole journey on the owner's phone (same loop as /early-access), in the demo brand or theirs */}
        <div className="relative min-w-0 lg:col-span-6" data-testid="hero-loop-column">
          <Rise delay={0.3} duration={1.2} y={28} reduced={reduced} play={play}>
            <JourneyLoop brand={brand || undefined} testId="hero-journey-loop" />
          </Rise>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/* One editorial sentence; the partner logos sit inside it as pills.   */
function useLogoScale() {
  const [sc, setSc] = useState(1);
  useEffect(() => {
    const f = () => setSc(window.innerWidth < 640 ? 0.72 : window.innerWidth < 1024 ? 0.9 : 1.08);
    f();
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return sc;
}
const TRUST_TOKENS = [
  // PMS handover only (no DigiHome logo — it is the product brand, not a PMS): created as a controlled draft in Guesty/Hostaway, linked in Lodgify/Smoobu.
  ["t", "l5.trust.s1"], ["l", "guesty"], ["t", "l5.trust.or"], ["l", "hostaway"], ["t", "l5.trust.s2"], ["l", "lodgify"], ["t", "l5.trust.or"], ["l", "smoobu"], ["p", "l5.trust.end"],
];
function TrustLine() {
  const sc = useLogoScale();
  return (
    <section className="mt-12 sm:mt-24" data-testid="trust-strip" aria-label={t("lp.nav.integrations")}>
      <div className={CONTAINER}>
        <Reveal as="p" className={EYEBROW}>{t("l5.trust.eyebrow")}</Reveal>
        <RevealGroup className="mt-5 max-w-[1120px] font-heading text-[24px] sm:text-[36px] lg:text-[46px] leading-[1.55] sm:leading-[1.45] tracking-[-0.03em] text-ink" stagger={0.045}>
          {TRUST_TOKENS.map(([kind, v], i) =>
            kind === "t" ? (
              <motion.span key={i} variants={revealItem} className="inline text-ink/80">{t(v)} </motion.span>
            ) : kind === "p" ? (
              <motion.span key={i} variants={revealItem} className="inline text-ink/80 -ml-1 sm:-ml-1.5">{t(v)} </motion.span>
            ) : (
              <motion.span key={i} variants={revealItem} className="inline-flex items-center align-middle mx-0.5 sm:mx-1 px-3 sm:px-4 h-[1.32em] -translate-y-[0.06em] rounded-full border border-hairline bg-surface text-ink/85 hover:text-ink hover:border-ink/30 hover:-translate-y-[0.14em] transition-[color,border-color,transform] duration-200 ease-editorial" data-testid={`trust-logo-${v}`}>
                <BrandLogo id={v} scale={sc * (v === "digihome" ? 1 : v === "googlemaps" ? 0.95 : 0.92)} />
              </motion.span>
            )
          )}
        </RevealGroup>
      </div>
    </section>
  );
}

/* ================================================================== */
/* Numbers band — the Rua da Prata story as four counters.             */
const NUMBERS = [1, 2, 3, 4];
function NumbersBand() {
  return (
    <section className="border-t border-hairline" data-testid="numbers-band" aria-label={t("l10.num.eyebrow")}>
      <div className={cn(CONTAINER, "py-12 sm:py-20")}>
        <Reveal as="p" className={EYEBROW}>{t("l10.num.eyebrow")}</Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 sm:gap-x-10">
          {NUMBERS.map((n, i) => (
            <Reveal key={n} delay={i * 0.06} y={14} className="min-w-0 lg:border-l lg:border-hairline lg:pl-6 first:border-0 first:pl-0" data-testid={`number-${n}`}>
              <p className="font-display text-[48px] sm:text-[64px] lg:text-[76px] text-ink tabular-nums leading-none">
                <Counter to={Number(t(`l10.num.${n}.value`))} suffix={t(`l10.num.${n}.suffix`)} duration={1100} />
              </p>
              <p className="mt-3 text-[15px] sm:text-[16px] font-medium text-ink leading-snug">{t(`l10.num.${n}.label`)}</p>
              <p className="mt-1.5 text-[13.5px] text-ink/55 leading-[1.55] max-w-[30ch]">{tb(`l10.num.${n}.sub`)}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
const FAQ = [1, 2, 3, 4, 5, 6];
function FaqSection() {
  return (
    <section id="faq" className="py-16 sm:py-32 border-t border-hairline scroll-mt-16" data-testid="faq-section">
      <div className={cn(CONTAINER, "grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-20")}>
        <div className="lg:sticky lg:top-28 self-start">
          <Reveal as="p" className={EYEBROW}>{t("l10.faq.eyebrow")}</Reveal>
          <Reveal as="h2" delay={0.04} className={cn(H2, "max-w-[12ch]")}>{t("l10.faq.title")}</Reveal>
          <Reveal as="p" delay={0.08} className={cn(SUB, "max-w-[40ch]")}>{tb("l10.faq.sub")}</Reveal>
          <Reveal delay={0.12} className="mt-8 hidden lg:block">
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-[14.5px] font-medium text-ink hover:text-lavender transition-colors" data-testid="faq-pricing-link">
              {t("l4.cta.pricing")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.06} y={14}>
          <Accordion type="single" collapsible className="divide-y divide-hairline border-y border-hairline" data-testid="faq-accordion">
            {FAQ.map((n) => (
              <AccordionItem key={n} value={`q${n}`} className="border-0 px-1" data-testid={`faq-item-${n}`}>
                <AccordionTrigger className="py-5 sm:py-6 text-left font-heading font-bold text-[17px] sm:text-[19px] text-ink tracking-[-0.02em] hover:no-underline hover:text-lavender transition-colors [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-ink/40" data-testid={`faq-trigger-${n}`}>
                  {t(`l10.faq.${n}.q`)}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-[15px] sm:text-[15.5px] text-ink/65 leading-[1.7] max-w-[62ch]">{tb(`l10.faq.${n}.a`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== */
function FinalCTA() {
  return (
    <section className="relative bg-ink overflow-hidden grain grain-dark" data-testid="cta-section" data-header-theme="dark">
      {/* one soft highlight, well under 20% of the viewport */}
      <div aria-hidden className="pointer-events-none hidden lg:block absolute -top-40 right-[-10%] h-[420px] w-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, var(--accent-glow), transparent 72%)", filter: "blur(18px)", opacity: 0.6 }} />
      <div className={cn(CONTAINER, "relative z-[2] py-16 sm:py-32 grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-8 lg:gap-16 items-end")}>
        <Reveal as="h2" y={12} className="font-display text-[36px] sm:text-[50px] lg:text-[60px] text-white max-w-[20ch]">{t("l4.cta.title")}</Reveal>
        <Reveal delay={0.08} y={12}>
          <p className="text-[16px] text-white/55 mb-8 max-w-[42ch] leading-[1.7]">{tb("l4.cta.sub")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/app/register"><PrimaryButton size="lg" data-testid="cta-register">{t("l4.cta.primary")}</PrimaryButton></Link>
            <Link to={`/start/${DEMO_SLUG}`} className="inline-flex items-center gap-1.5 rounded-[14px] text-white/65 hover:text-white h-[52px] px-5 text-[15px] transition-colors" data-testid="cta-demo">{t("l4.hero.demo")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <p className="mt-5 text-[12.5px] text-white/40 leading-relaxed" data-testid="cta-note">{t("l4.cta.note")}</p>
        </Reveal>
      </div>
    </section>
  );
}
